/**
 * Shared visual tokens for V2 Company Details.
 * One 8px-based spacing scale for header + all tabs.
 *
 * Scale (MUI spacing units → px):
 *   xs  0.5 →  4px   tight (label → value)
 *   sm  1   →  8px   within-group / icons
 *   md  1.5 → 12px   card padding / compact gaps
 *   lg  2   → 16px   cards, title→content, major groups
 *   xl  2.5 → 20px   page pad, tab→content
 *   xxl 3   → 24px   section separation
 */
export const SPACE = {
  xs: 0.5,
  sm: 1,
  md: 1.5,
  lg: 2,
  xl: 2.5,
  xxl: 3,
};

/**
 * Shared visual tokens for V2 Company Details tab content.
 * Metrics → Details → Location → People → Similar.
 */
export const SIDEBAR = {
  radius: "10px",
  radiusSm: "8px",
  border: "1px solid rgba(88, 89, 91, 0.10)",
  borderSoft: "1px solid rgba(88, 89, 91, 0.08)",
  shadow: "0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.04)",
  surface: "#FFFFFF",
  surfaceMuted: "rgba(88, 89, 91, 0.045)",
  surfaceReadonly: "rgba(88, 89, 91, 0.055)",

  /** Outer drawer horizontal padding */
  pagePadX: { xs: SPACE.lg, md: SPACE.xl },
  /** Outer drawer top padding */
  pagePadTop: SPACE.lg,

  /** Content padding under the tab rail — top and end-of-content bottom match. */
  contentPadX: 0,
  contentPadTop: SPACE.xl,
  contentPadBottom: SPACE.xl,

  /** Card internal padding */
  cardPadX: SPACE.md,
  cardPadY: SPACE.md,

  /** Vertical rhythm */
  gap: {
    section: SPACE.xxl,
    sectionTitleToContent: SPACE.lg,
    labelToValue: SPACE.xs,
    card: SPACE.lg,
    withinCard: SPACE.sm,
    listItem: SPACE.md,
    field: SPACE.lg,
    chip: SPACE.sm,
  },

  /**
   * Metrics tab blocks — ~20% taller than the original Pin compact cards,
   * with extra vertical padding so label / value / chart stay hierarchical.
   */
  metrics: {
    summaryMinHeight: 100,
    summaryPadY: SPACE.lg,
    summaryValueSize: 20,
    chartPadY: SPACE.lg,
    rangeHeight: 116,
    revenueHeight: 106,
    compareHeight: 144,
    compactRangeHeight: 192,
    compactRevenueHeight: 178,
    compactCompareHeight: 202,
    barMaxSize: 53,
    compactBarMaxSize: 67,
  },

  type: {
    sectionTitle: {
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: "-0.01em",
      lineHeight: 1.25,
      color: "text.primary",
    },
    label: {
      fontSize: 11.5,
      fontWeight: 600,
      letterSpacing: "0.01em",
      lineHeight: 1.3,
      color: "text.secondary",
    },
    body: {
      fontSize: 13,
      fontWeight: 400,
      lineHeight: 1.55,
      color: "text.primary",
    },
    bodySecondary: {
      fontSize: 13,
      fontWeight: 400,
      lineHeight: 1.55,
      color: "text.secondary",
    },
    value: {
      fontSize: 18,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      lineHeight: 1.15,
      color: "text.primary",
    },
    meta: {
      fontSize: 11.5,
      fontWeight: 500,
      lineHeight: 1.3,
      color: "text.secondary",
    },
  },
};
